import {formatCopy} from 'utils/typography';
import React, {useEffect, useRef, useState} from "react";
import {Controller, useFormContext} from 'react-hook-form'
import { Trans, useTranslation } from "react-i18next"
import Link from "@mui/material/Link";
import debounce from "lodash/debounce";

import {InputAdornment} from "@mui/material";
import TextField from '@mui/material/TextField'
import Autocomplete from "@mui/material/Autocomplete";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { LocationOn as LocationOnIcon } from '@vedmarket/ui-kit';

import { InvoiceForm, INVOICE_FORM } from '../../editInvoice'
import {COLORS} from "../../../../../../../../../../../../config/constants";
import {useLazyGetLocationsQuery, useLazyGetLocationByIdQuery} from "../../../../../../../../../../../../api/locations";
import { LocalShipping, LocationCity, LocationSearching, Flight, DirectionsBoat, Train } from "@vedmarket/ui-kit";
import LocationFlag from 'components/common/location-flag';
import {useLazyGetCoordsQuery} from "../../../../../../../../../../../../api/geocoding";

import Map from '../locationMap'
import {isDemoPage} from "../../../../../../../../../../../../utils/hooks";
import {useDemoInvoiceLocation} from 'components/cabinet/demo/steps/packaging/use-invoice';
import {LinkPopover} from '@vedmarket/ui-kit';

import './locationInput.scss'

enum LOCATION_TYPE {
  PORT = 'port',
  CITY = 'city',
  RAIL_TERMINAL = 'rail terminal',
  AIRPORT = 'airport',
  ROAD_TERMINAL = 'road terminal',
  POSTAL_OFFICE = 'postal exchange office',
  MULTIMODAL_FUNCTION = 'multimodal functions (icds, etc.)',
  FIXED_TRANSPORT_FUNCTIONS = 'fixed transport functions (e.g. oil platform)',
  INLAND_PORT = 'inland port',
  BORDER_CROSSING = 'border crossing',
}

const locationIconStyle = {
  color: COLORS.LIGHT_ACTION,
  fill: 'currentColor',
  width: '24px',
  height: '24px',
};

type OptionData = {
  label: string
}

type LocationItemDefaultCity = LocationItem & OptionData;

type Props = {
  defaultCity?: LocationItemDefaultCity
}

const LocationInput  = ({defaultCity} : Props) => {

  const isDemoMode = isDemoPage()

  const { setValue, control, formState: { errors } } = useFormContext<InvoiceForm>()

  const [ lat, setLat ] = React.useState<number | undefined>();

  const [ lng, setLng ] = React.useState<number | undefined>();

  const [ selectedLocation, setSelectedLocation ] = React.useState<LocationItemDefaultCity | null>(isDemoMode ? null : defaultCity || null);

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [ geocodingRequest ] = useLazyGetCoordsQuery()

  const [ locationRequest, { data: dataLocations, isLoading: isLoadingCities, isSuccess: isSuccessCities } ] = useLazyGetLocationsQuery()
  const [ locationByIdRequest ] = useLazyGetLocationByIdQuery()

  const debouncedRequest = useRef(debounce(locationRequest, 1000)).current

  const getLocationsWithoutCities = () => (dataLocations || [])
    .filter(item => item.type !== LOCATION_TYPE.CITY)
    .map(item => ({
      ...item,
      group: JSON.stringify({
        groupName: item.city.name,
        groupCode: item.city.code,
        groupCountryCode: item.city.country.iso2 || item.city.country.code,
        groupCountryName: item.city.country.name,
        groupId: item.city.id,
      }),
      label: `${item.name}, ${item.country.name} ${item.city.name}`
    }));

  const getLocationsCitiesOnly = () => {

    const groupedCityIds = new Set(
      (dataLocations || [])
        .filter(item => item.type !== LOCATION_TYPE.CITY && item.city?.id)
        .map(item => item.city.id)
    );

    return (dataLocations || [])
      .filter(item => item.type === LOCATION_TYPE.CITY && !groupedCityIds.has(item.id))
      .map(item => ({
        ...item,
        group: JSON.stringify({
          groupName: item.name,
          groupCode: item.code,
          groupCountryCode: item.country.iso2 || item.country.code,
          groupCountryName: item.country.name,
          groupId: item.id,
          type: LOCATION_TYPE.CITY,
        }),
        label: `${item.name}, ${item.country.name}`
      }));
  }

  const locationsWithoutCities = getLocationsWithoutCities();
  const locationsWithCitiesOnly = getLocationsCitiesOnly();

  const locations = [ ...locationsWithoutCities, ...locationsWithCitiesOnly ]

  const [ defaultValue ] = useState<LocationItemDefaultCity | undefined>(defaultCity)

  const [ userHasStartedTyping, setUserHasStartedTyping ] = useState(false);
  const shouldShowLocations = userHasStartedTyping && isSuccessCities;

  const { t } = useTranslation()

  useEffect(() => {
    if (defaultCity && !isDemoMode) {
      setLat(defaultCity.lat)
      setLng(defaultCity.lng)
    }
  }, [ defaultCity, isDemoMode ])

  const setLatLng = (lat?: number, lng?: number) => {
    if (lat && lng) {
      setLat(lat);
      setLng(lng);
      return true;
    }
    return false;
  };

  const handleSetAutocomplete = async (value: any, reason: string) => {

    if (reason === 'clear') {
      setUserHasStartedTyping(false);
    }

    if (!value) return;

    setSelectedLocation(value);

    if (setLatLng(value.lat, value.lng)) {
      setValue(INVOICE_FORM.LOCATION, value.id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    const cityId = value.id.split("city_")[1];
    const res = await locationByIdRequest(cityId);
    const currentCity = res.data;

    if (setLatLng(currentCity?.lat, currentCity?.lng)) {
      setValue(INVOICE_FORM.LOCATION, value.id, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      return;
    }

    const geoRes = await geocodingRequest(value.city.name);
    const feature = geoRes?.data?.features?.[0];
    setLatLng(feature?.center?.[1], feature?.center?.[0]);

    setValue(INVOICE_FORM.LOCATION, value.id, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
  }

  const demoPlayback = useDemoInvoiceLocation({
    defaultCity,
    inputRef,
    locations,
    locationRequest,
    onChoose: location => handleSetAutocomplete(location, 'selectOption'),
    onSearchComplete: () => setUserHasStartedTyping(true),
  })

  const getIcon = (type: string) => {
    switch (type) {
      case LOCATION_TYPE.AIRPORT:
        return <Flight className='location-option__sign'/>
      case LOCATION_TYPE.RAIL_TERMINAL:
        return <Train className='location-option__sign'/>
      case LOCATION_TYPE.PORT:
        return <DirectionsBoat className='location-option__sign'/>
      case LOCATION_TYPE.ROAD_TERMINAL:
        return <LocalShipping className='location-option__sign'/>
      case LOCATION_TYPE.CITY:
        return <LocationCity className='location-option__sign'/>
      default:
        return null;
    }
  }

  const handleGroupClick = async (id: string) => {

    setValue(INVOICE_FORM.LOCATION, id, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });

    inputRef.current?.blur();

    const cityId = id.split("city_")[1];

    await locationByIdRequest(cityId).then( async (res) => {

      const currentCity = res.data;
      if (!currentCity) return;
      // @ts-ignore
      setSelectedLocation({...currentCity, label: `${currentCity?.name}, ${currentCity?.country?.name} ${currentCity?.city?.name}`});

      if (currentCity?.lat && currentCity?.lng) {
        setLat(currentCity?.lat);
        setLng(currentCity?.lng);
      } else {
        const geoRes = await geocodingRequest(currentCity.name);
        const feature = geoRes?.data?.features?.[0];
        setLatLng(feature?.center?.[1], feature?.center?.[0]);
      }

    });

  }

  return (
    <>
      <div className='invoice-field invoice-field_wide'>
        <Controller
          name={INVOICE_FORM.LOCATION}
          control={control}
          rules={{ required: 'Поле обязательно для заполнения' }}
          render={({ field }) => (
            <Autocomplete
              data-demo-search-query={demoPlayback.searchQuery}
              className={`invoice-form__${INVOICE_FORM.LOCATION}`}
              options={locations}
              loading={isLoadingCities}
              value={selectedLocation}
              {...demoPlayback.autocompleteProps}
              noOptionsText={t('calc__text__main__noOptionsFound') }
              renderOption={(props, option) => (
                <li {...props} key={option.id} className='location-option'>
                  <span className='location-option__image'>{getIcon(option.type)}</span>
                  <Typography variant='body2' sx={{
                    fontSize: '14px',
                    lineHeight: '21px',
                    letterSpacing: 0,
                    color: COLORS.BLACK_DARK
                  }}>
                    <span className='location-option__name'>
                      {option.name}
                    </span>
                    <span className='location-option__id'>
                      {option.id}
                    </span>
                  </Typography>
                </li>
              )}
              // @ts-ignore
              groupBy={(option) => option.group}
              renderGroup={(params) => {
                if (!params.group) return null;
                const meta = JSON.parse(params.group);
                return (
                  <li key={params.key} className='location-group'>
                    <div className='location-group__main' onClick={() => handleGroupClick(meta.groupId)}>
                      <div className='location-group__flag'>
                        <LocationFlag code={meta.groupCountryCode} />
                      </div>
                      <div className='location-group__info'>
                        <Typography variant='body1' sx={{
                          lineHeight: '24px',
                          letterSpacing: 0,
                          color: COLORS.BLACK_DARK,
                        }}>
                          <span className='location-group__name'>
                            {meta.groupName}
                          </span>
                          <span className='location-group__code'>
                            {meta.groupCode}
                          </span>
                        </Typography>
                        <Typography variant='caption' sx={{
                          fontSize: '12px',
                          lineHeight: '12px',
                          letterSpacing: 0,
                          color: COLORS.LIGHT_TEXT_DISABLED,
                        }}>
                          {meta.groupCountryName}
                        </Typography>
                      </div>
                    </div>
                    {meta.type !== LOCATION_TYPE.CITY && params.children}
                  </li>
                )
              }}
              filterOptions={(options) => options}
              PaperComponent={({ children }) => {
                if (!shouldShowLocations) return null;

                return (
                  <Paper elevation={8} sx={{
                    borderRadius: 0,
                    marginTop: '8px',
                    '& [role="option"].Mui-focused': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '& .MuiAutocomplete-listbox': {
                      padding: '8px 0',
                      maxHeight: 'calc(var(--calculator-popup-height, 100vh) - 8px)',
                    },
                  }}>
                    {children}
                  </Paper>
                )
              }}
              renderValue={(value, getItemProps) => {
                if (!value) return null;

                const icon = getIcon(value.type);

                const name = value?.city?.name || value?.name;

                return <Typography component='div' variant='body1' {...getItemProps()} className='location-value' sx={{
                  lineHeight: '24px',
                  letterSpacing: 0,
                }}>
                  <span className='location-value__flag'>
                    <LocationFlag code={value.country.iso2 || value.country.code} />
                  </span>
                  <span className='location-value__city'>
                    {name && <span className='location-value__name'>{name}</span>}
                    <span className='location-value__code'>{value.code}</span>
                  </span>
                  {value?.city?.name && icon && (
                    <span className='location-value__image'>
                      {icon}
                    </span>
                  )}
                  {value?.city?.name && <span className='location-value__detail'>
                    <span className='location-value__location-name'>
                      {value.name}
                    </span>
                    <span className='location-value__id'>
                      {value.id}
                    </span>
                  </span>}
                </Typography>;
              }}
              // @ts-ignore
              onChange={(_, value, reason) => {
                field.onChange(value);
                handleSetAutocomplete(value, reason);
              }}
              isOptionEqualToValue={(o: LocationItemDefaultCity, v: LocationItemDefaultCity) => o.id === v.id}
              loadingText={t('common__message__loadingText')}
              renderInput={(params) => {
                return <TextField
                  {...params}
                  error={Boolean(errors[INVOICE_FORM.LOCATION])}
                  placeholder=''
                  inputRef={inputRef}
                  variant='outlined'
                  label={t('calc__text__main__cityOfDeliveryExplanation')}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !params.inputProps.value) {
                      setSelectedLocation(null);
                      field.onChange(null);
                    }
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    params.inputProps.onBlur?.(
                        e as React.FocusEvent<HTMLInputElement>
                    );
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {params.inputProps.value && <InputAdornment position='start' sx={{ marginRight: 0 }}>
                          <LocationOnIcon style={locationIconStyle} />
                        </InputAdornment>}
                        {!params.inputProps.value && params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <InputAdornment position='end' sx={{
                        right: '12px',
                        position: 'absolute',
                        margin: 0,
                      }}>
                        <LocationSearching style={locationIconStyle} />
                      </InputAdornment>
                    ),
                  }}
                  onChange={ async (e) => {

                    params.inputProps.onChange?.(
                        e as React.ChangeEvent<HTMLInputElement>
                    );

                    const query = (e.target as HTMLInputElement | HTMLTextAreaElement).value;

                    if (query.length >= 3) {
                      await debouncedRequest({ q: query, include_extra: true });
                      setUserHasStartedTyping(true);
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                      padding: 0,
                      lineHeight: '24px',
                      letterSpacing: 0,
                    },
                    '& .MuiInputBase-input:placeholder-shown': {
                      minWidth: '1px',
                      width: '1px',
                      position: 'absolute',
                      left: '12px',
                      padding: 0,
                    },
                    '& .MuiInputBase-input:not(:placeholder-shown)': {
                      minWidth: '30px',
                      width: '30px',
                      zIndex: 0,
                    },
                    '& .MuiOutlinedInput-root.MuiInputBase-adornedEnd': {
                      height: '48px',
                      minHeight: '48px',
                      padding: '12px 44px 12px 12px',
                      gap: '8px',
                    }
                  }}
                />
              }}/>
          )}
        />
        <LinkPopover
          linkText={t('calc__help__invoiceLocationLink')}
          text={<Trans
            defaults={formatCopy(t('calc__help__invoiceLocation'))}
            components={{
              support: <Link
                href='mailto:demo@vedmarket.ru'
                variant='inherit'
                underline='hover'
                sx={{
                  color: COLORS.BLUE,
                  cursor: 'pointer',
                }}
              />,
            }}
          />}
        />
      </div>
      {lng && lat && <Map lng={lng} lat={lat} />}
    </>
  )
}



export default LocationInput
